import { defaultDateFormat, parseDate } from "@lyfegen-platform/utils";
import { format } from "date-fns";
import * as Yup from "yup";
import { ControlState } from "../models/flexible/control-state";
import { DataType } from "../models/flexible/data-type";
import { DataControllerProps, FlexControlProps, FlexFieldController } from "../models/flexible/flex-field-controller";
import { FlexFieldSchema } from "../models/flexible/flex-field-schema";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { StatusHistorical } from "../models/flexible/status-historical";
import { Maybe } from "../models/generated/graphql";
import { isEmptyValue } from "../utils/is-empty-value";
import { renderMaxCharactersErrorMessage } from "../utils/render-max-characters-message";
import { renderMaxErrorMessage } from "../utils/render-max-message";
import { renderMinCharactersErrorMessage } from "../utils/render-min-characters-message";
import { renderMinErrorMessage } from "../utils/render-min-message";
import { renderRequiredMessage } from "../utils/render-required-message";

export class GeneralFieldController implements FlexFieldController {
	parent: FlexSectionController = null as any;

	constructor(readonly schema: FlexFieldSchema, readonly path: string) {}

	get mappedFieldName(): string {
		return this.schema.attribute.identifier;
	}

	mapInitialValue(value: unknown): unknown {
		if (isEmptyValue(value)) {
			return this.schema.attribute.dataType === DataType.Boolean ? false : "";
		}

		if (this.schema.attribute.dataType === DataType.Date) {
			return format(parseDate(value as string), defaultDateFormat);
		}

		return value;
	}

	createValidationRule(_initialData: unknown, translation: (key: string) => string): Yup.AnySchema {
		return this.schema.attribute.validation !== StatusHistorical.Optional
			? this.createFieldValidationRuleByDataType(translation)
					.nullable()
					.required(renderRequiredMessage(this.schema.attribute.identifier, translation))
			: this.createFieldValidationRuleByDataType(translation).nullable();
	}

	useControlProps(path: string): Maybe<FlexControlProps> | undefined {
		return null;
	}

	useControlState(path: string): ControlState {
		return "enabled";
	}

	mapFormValue(value: unknown): unknown {
		return isEmptyValue(value) ? null : value;
	}

	getValueTypeProps(): Maybe<DataControllerProps> | undefined {
		return null;
	}

	protected createFieldValidationRuleByDataType(translation: (key: string) => string): Yup.AnySchema {
		switch (this.schema.attribute.dataType) {
			case DataType.Boolean:
				return Yup.boolean();

			case DataType.Date:
				return Yup.date().typeError(translation("invalid-date"));

			case DataType.Number: {
				const numberSchema = this.schema.attribute.decimalPrecision
					? Yup.number()
					: Yup.number().integer(translation("must_be_integer"));
				const minApplied = this.schema.attribute.minLength
					? numberSchema.min(Math.pow(10, this.schema.attribute.minLength - 1), renderMinErrorMessage(translation))
					: numberSchema;
				const maxApplied = this.schema.attribute.maxLength
					? minApplied.max(Math.pow(10, this.schema.attribute.maxLength) - 1, renderMaxErrorMessage(translation))
					: minApplied;
				return maxApplied;
			}
			case DataType.Text: {
				const stringSchema = Yup.string().trim();
				const minLengthApplied = this.schema.attribute.minLength
					? stringSchema.min(this.schema.attribute.minLength, renderMinCharactersErrorMessage(translation))
					: stringSchema;
				const maxLengthApplied = this.schema.attribute.maxLength
					? minLengthApplied.max(this.schema.attribute.maxLength, renderMaxCharactersErrorMessage(translation))
					: minLengthApplied;
				return maxLengthApplied;
			}
			default:
				return Yup.object();
		}
	}
}
